import Service from '../service';
import Request from '../util/request';

export default class Auth {
    public verified: string | null = null;

    public timestamp: number = 0;
    public permissionsTimestamp: number = 0;
    public status: any = null;
    public loading: any = null;
    public session: any = {};
    public permissions: any = {};
    public request: Request;

    private initPromise: Promise<Auth> | null = null;
    private permissionsPromise: Promise<any> | null = null;
    private permissionsLoaded: boolean = false;
    private readonly permissionsTtl: number = 1000 * 60 * 5;

    constructor(public service: Service) {
        this.request = new Request();
    }

    public async init(force: boolean = false) {
        const now = new Date().getTime();
        if (!force && this.loading === true && now - this.timestamp <= 1000 * 60) {
            return this;
        }
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this.fetchSession(force);
        try {
            return await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    private async fetchSession(force: boolean = false) {
        try {
            let { code, data } = await this.request.post('/auth/check');
            let { status, session, permissions, permissions_loaded } = data || {};
            session = session || {};
            const now = new Date().getTime();
            this.status = false;
            this.session = {};
            this.permissions = {};
            this.permissionsLoaded = false;
            this.verified = session.verified;
            this.loading = true;
            if (code != 200)
                return this;
            this.timestamp = now;
            this.session = session;
            this.status = status;
            if (permissions_loaded && permissions && typeof permissions === 'object') {
                this.permissions = permissions;
                this.permissionsTimestamp = now;
                this.permissionsLoaded = true;
            } else {
                await this.loadPermissions(force);
            }
        } catch (e) {
            this.status = false;
            this.session = {};
            this.permissions = {};
            this.permissionsLoaded = false;
            this.loading = true;
        }
        return this;
    }

    public async update() {
        while (this.loading === null) {
            await this.service.render(100);
        }

        let diff = new Date().getTime() - this.timestamp;
        if (diff > 1000 * 60) {
            this.loading = null;
            await this.init(true);
        }
    }

    public check: any = new Proxy(((root) => {
        let obj: any = () => {
            return this.status;
        }

        obj.root = root;
        return obj;
    })(this), {
        get(target, propKey) {
            let propValue: any = target.root.session[propKey];

            return (values: any = null) => {
                if (values === null) {
                    return true;
                }

                if (typeof values == 'boolean') {
                    if (values === propValue)
                        return true;
                    return false;
                }

                if (typeof values == 'string')
                    values = [values];

                if (values.indexOf(propValue) >= 0) {
                    return true;
                }

                return false;
            };
        }
    });

    public allow: any = new Proxy(((root) => {
        let obj: any = (redirect: any = null) => {
            if (root.check()) return true;
            if (redirect) location.href = redirect;
            return false;
        }

        obj.root = root;
        obj.check = root.check;
        return obj;
    })(this), {
        get(target, propKey) {
            return (values: any = null, redirect: any = null) => {
                if (target.check[propKey](values)) return true;
                if (redirect) location.href = redirect;
                return false;
            }
        }
    });

    public async loadPermissions(force: boolean = false) {
        const now = new Date().getTime();
        if (!force && this.permissionsLoaded && now - this.permissionsTimestamp <= this.permissionsTtl) {
            return this.permissions;
        }
        if (this.permissionsPromise) {
            await this.permissionsPromise;
            return force ? this.loadPermissions(true) : this.permissions;
        }

        this.permissionsPromise = this.fetchPermissions();
        try {
            return await this.permissionsPromise;
        } finally {
            this.permissionsPromise = null;
        }
    }

    private async fetchPermissions() {
        this.permissions = {};
        this.permissionsTimestamp = new Date().getTime();
        this.permissionsLoaded = true;
        if (!this.status) {
            return this.permissions;
        }

        try {
            const response = await fetch('/wiz/api/page.members/sidebar_policy', {
                method: 'GET',
                cache: 'no-store',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                return this.permissions;
            }
            const payload = await response.json();
            const data = payload?.data || payload || {};
            this.permissions = data?.permissions || {};
            this.permissionsTimestamp = new Date().getTime();
        } catch (e) {
            this.permissions = {};
            this.permissionsTimestamp = new Date().getTime();
        }

        return this.permissions;
    }

    public can(key: string = '', action: string = 'read') {
        if (!key) {
            return true;
        }

        const normalizedRole = String(this.session?.role || '').toLowerCase();
        if (normalizedRole === 'superadmin') {
            return true;
        }

        const actions = this.permissions?.[key];
        if (!actions || typeof actions !== 'object') {
            return false;
        }
        if (action !== 'read' && !actions.read) {
            return false;
        }
        return !!actions[action];
    }

    public hash(password: string = '') {
        return this.service.crypto.SHA256(password).toString();
    }
}
